import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import {
  Box,
  Typography,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  useTheme,
  ButtonBase,
  useMediaQuery,
} from "@mui/material";

// helper to format time mm:ss
const fmt = (t) => new Date(t * 1000).toISOString().substr(14, 5);

const tagChipSx = {
  borderRadius: 999,
  px: 1.4,
  height: 24,
  fontSize: "0.7rem",
  textTransform: "none",
  borderColor: "rgba(255,255,255,0.35)",
  bgcolor: "rgba(255,255,255,0.06)",
};

const PedalDialog = ({
  open,
  pedal,
  onClose,
  onPrevPedal,
  onNextPedal,
  canCycle,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [selectedModeId, setSelectedModeId] = React.useState(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [duration, setDuration] = React.useState(0);
  const [time, setTime] = React.useState(0);
  const [isLooping, setIsLooping] = React.useState(true);
  const [isScrubbing, setIsScrubbing] = React.useState(false);

  // refs for all audio elements (hidden)
  const audioRefs = React.useRef([]);
  const getAudioRef = (i) => (el) => {
    audioRefs.current[i] = el;
  };

  // Derive "modes" list (could be variants/modes/mics – pick one in your data)
  const modes =
    pedal?.variants || pedal?.modes || pedal?.mics || []; // <- adjust to your data model
  const selectedModeIndexRaw = modes.findIndex((m) => m.id === selectedModeId);
  const selectedModeIndex =
    selectedModeIndexRaw === -1 ? 0 : selectedModeIndexRaw;
  const selectedMode = modes[selectedModeIndex] || modes[0] || null;
  const modeCount = modes.length || 1;
  const segmentWidthDesktop = 100 / modeCount;

  // ---------- MOBILE MODE WINDOW / NAV LOGIC ----------
  const [modeStartIndex, setModeStartIndex] = React.useState(0);

  const visibleModeCount = isMobile
    ? Math.min(3, modes.length || 0)
    : modes.length || 0;

  const showModeNav = isMobile && modes.length > visibleModeCount;

  // Reset window when dialog opens or layout mode changes
  React.useEffect(() => {
    if (!open) return;
    setModeStartIndex(0);
  }, [open, isMobile]);

  React.useEffect(() => {
    if (!showModeNav) {
      setModeStartIndex(0);
      return;
    }
    if (!modes.length) return;

    setModeStartIndex((prev) => {
      if (selectedModeIndex < prev) return selectedModeIndex;
      if (selectedModeIndex >= prev + visibleModeCount) {
        return selectedModeIndex - visibleModeCount + 1;
      }
      return prev;
    });
  }, [selectedModeIndex, showModeNav, visibleModeCount, modes.length]);

  const visibleModes = React.useMemo(() => {
    if (!modes.length) return [];
    if (!showModeNav) return modes;
    return modes.slice(modeStartIndex, modeStartIndex + visibleModeCount);
  }, [modes, showModeNav, modeStartIndex, visibleModeCount]);

  const visibleIndex = showModeNav
    ? selectedModeIndex - modeStartIndex
    : selectedModeIndex;

  const mobileSegmentCount = visibleModes.length || 1;
  const mobileSegmentWidth = 100 / mobileSegmentCount;
  const mobileHighlightLeft =
    visibleIndex >= 0 && visibleIndex < mobileSegmentCount
      ? visibleIndex * mobileSegmentWidth
      : 0;

  // nav availability based on selected mode
  const canNavLeft = showModeNav && selectedModeIndex > 0;
  const canNavRight = showModeNav && selectedModeIndex < modes.length - 1;

  // nav buttons select previous/next mode (window follows via effect)
  const handleModeNav = (dir) => {
    if (!modes.length) return;
    const curr = selectedModeIndex;
    if (dir === "left" && curr > 0) {
      onModeButtonClick(curr - 1);
    } else if (dir === "right" && curr < modes.length - 1) {
      onModeButtonClick(curr + 1);
    }
  };

  // ---------- OPEN/CLOSE LIFECYCLE ---------------------------------

  React.useEffect(() => {
    if (open && pedal && (pedal.variants || pedal.modes || pedal.mics)?.length) {
      const list = pedal.variants || pedal.modes || pedal.mics;
      setSelectedModeId((prev) => {
        if (prev && list.some((m) => m.id === prev)) return prev;
        return list[0].id;
      });
    }

    // Reset when dialog closes
    if (!open) {
      setSelectedModeId(null);
      setIsPlaying(false);
      setTime(0);
      setDuration(0);
      if (audioRefs.current.length) {
        audioRefs.current.forEach((a) => {
          if (!a) return;
          a.pause();
          a.currentTime = 0;
        });
      }
    }
  }, [open, pedal]);

  // Reset duration when pedal changes (but keep time)
  React.useEffect(() => {
    if (!pedal) return;
    setDuration(0);
  }, [pedal]);

  // ---------- PEDAL CHANGE: behave like mode switch ----------------

  React.useEffect(() => {
    if (!open || !pedal) return;
    const list = pedal.variants || pedal.modes || pedal.mics;
    if (!list?.length) return;

    // pick mode id: keep same id if present, otherwise first
    setSelectedModeId((prev) => {
      if (prev && list.some((m) => m.id === prev)) return prev;
      return list[0].id;
    });

    // align new pedal's tracks to current time
    const t = time;
    audioRefs.current.forEach((a) => {
      if (!a) return;
      try {
        a.currentTime = t;
      } catch {
        /* ignore */
      }
    });

    // if we were playing before, keep playing on new pedal
    if (isPlaying) {
      audioRefs.current.forEach((a, i) => {
        if (!a) return;
        const isActive = i === selectedModeIndex;
        a.loop = isLooping;
        a.playsInline = true;
        a.muted = !isActive;
        a.volume = isActive ? 1 : 0;
      });

      Promise.all(
        audioRefs.current.map(async (a) => {
          if (!a) return;
          if (a.paused) {
            try {
              await a.play();
            } catch {
              // Safari may block; nothing we can do without user gesture
            }
          }
        })
      ).then(() => {
        setIsPlaying(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedal, open]);

  // ---------- AUDIO METADATA & AUDIBILITY ---------------------------

  // Collect duration from all tracks (take max)
  const handleLoadedMeta = (e) => {
    const d = e.currentTarget.duration;
    if (!Number.isNaN(d) && d !== Infinity) {
      setDuration((prev) => Math.max(prev, d));
    }
  };

  // Keep only the selected mode audible
  React.useEffect(() => {
    audioRefs.current.forEach((a, i) => {
      if (!a) return;
      const isActive = i === selectedModeIndex;
      a.muted = !isActive;
      a.volume = isActive ? 1 : 0;
    });
  }, [selectedModeIndex, modes.length]);

  // ---------- PLAYBACK CONTROL (Safari-friendly, no auto reseek) ----

  const playAll = async () => {
    audioRefs.current.forEach((a, i) => {
      if (!a) return;
      const isActive = i === selectedModeIndex;
      a.loop = isLooping;
      a.playsInline = true;
      a.muted = !isActive;
      a.volume = isActive ? 1 : 0;
    });

    try {
      await Promise.all(
        audioRefs.current.map(async (a) => {
          if (!a) return;
          if (a.paused) await a.play();
        })
      );
      setIsPlaying(true);
    } catch {
      // Safari might block autoplay without gesture
    }
  };

  const pauseAll = () => {
    audioRefs.current.forEach((a) => a && a.pause());
    setIsPlaying(false);
  };

  const restartAll = () => {
    const wasPlaying = isPlaying;
    pauseAll();
    audioRefs.current.forEach((a) => {
      if (a) a.currentTime = 0; // explicit position change
    });
    setTime(0);
    if (wasPlaying) playAll();
  };

  const commitSeek = (t) => {
    const wasPlaying = isPlaying;
    pauseAll();
    audioRefs.current.forEach((a) => {
      if (a) a.currentTime = t; // explicit position change
    });
    setTime(t);
    if (wasPlaying) playAll();
  };

  const togglePlay = () => {
    if (isPlaying) pauseAll();
    else playAll(); // no seeking here
  };

  const toggleLoop = () => {
    const nextLoop = !isLooping;
    setIsLooping(nextLoop);
    audioRefs.current.forEach((a) => {
      if (a) a.loop = nextLoop;
    });
  };

  // Mode switch: align all tracks to this mode’s currentTime
  const onModeButtonClick = (idxGlobal) => {
    const targetMode = modes[idxGlobal];
    if (!targetMode) return;

    const master = audioRefs.current[idxGlobal];
    const newTime = master?.currentTime || time;

    audioRefs.current.forEach((a) => {
      if (!a) return;
      try {
        a.currentTime = newTime; // explicit position change: mode switch
      } catch {
        /* ignore */
      }
    });

    setTime(newTime);
    setSelectedModeId(targetMode.id);
  };

  // UI time: just read from current mode, never reseek
  React.useEffect(() => {
    if (!isPlaying) return;
    const id = setInterval(() => {
      const master = audioRefs.current[selectedModeIndex];
      if (!master) return;
      setTime(master.currentTime || 0);
    }, 200);
    return () => clearInterval(id);
  }, [isPlaying, selectedModeIndex]);

  // All hooks above this line
  if (!pedal) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          bgcolor: "rgba(24, 24, 28, 0.98)",
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow:
            "0 22px 60px rgba(0,0,0,0.95), 0 0 0 1px rgba(255,255,255,0.08)",
          maxHeight: "80vh",
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          pr: 6,
          py: 1.2,
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {pedal.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              opacity: 0.75,
              display: "block",
              mt: 0.3,
            }}
          >
            {pedal.subtitle}
          </Typography>
        </Box>

        <IconButton aria-label="close" onClick={onClose} sx={{ ml: "auto" }}>
          <CloseIcon sx={{ color: "white" }} fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          py: 2.5,
          flex: 1,
          overflowY: "auto",
        }}
      >
        {/* Centered content column */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          {/* Pedal image + big arrows overlaid */}
          <Box
            sx={{
              width: "100%",
              maxWidth: 400,
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid",
              borderColor: theme.palette.divider,
              mb: 1.5,
              position: "relative",
              flexShrink: 0,
            }}
          >

            <Box
              component="img"
              src={pedal.image}
              alt={pedal.name}
              sx={{
                display: "block",
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>

          {/* Tag chips – pill-shaped bubbles */}
          {pedal.tags && pedal.tags.length > 0 && (
            <Stack
              direction="row"
              spacing={0.75}
              flexWrap="wrap"
              justifyContent="center"
              sx={{ mb: 1.5 }}
            >
              {pedal.tags.map((tag) => (
                <Chip
                  key={tag}
                  size="small"
                  label={tag}
                  variant="outlined"
                  sx={tagChipSx}
                />
              ))}
            </Stack>
          )}

          {/* Optional amp label if you still use it */}
          {selectedMode && selectedMode.amp && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                opacity: 0.8,
                mb: 1.5,
              }}
            >
              Amp used for these demos: {selectedMode.amp}
            </Typography>
          )}

          {/* Intro text */}
          <Typography
            variant="body1"
            sx={{
              opacity: 0.85,
              color: theme.palette.text.secondary,
              lineHeight: 1.5,
              maxWidth: 800,
              mb: 2.5,
            }}
          >
            Choose a mode below to hear how it shapes this pedal. Each mode has
            its own voicing and feel, giving you different flavors from the same
            stompbox.
          </Typography>

          {/* Mode selection – DESKTOP: original pill bar; MOBILE: windowed with nav */}
          {modes.length > 0 && (
            <>
              {isMobile ? (
                // -------- MOBILE VERSION ----------
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 480,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2.5,
                    gap: 1,
                  }}
                >
                  {/* Left nav (selects prev mode) */}
                  {showModeNav && (
                    <IconButton
                      size="small"
                      onClick={() => handleModeNav("left")}
                      disabled={!canNavLeft}
                      sx={{
                        bgcolor: "rgba(0,0,0,0.7)",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.95)" },
                        borderRadius: "999px",
                        p: 0.5,
                        visibility: showModeNav ? "visible" : "hidden",
                      }}
                    >
                      <NavigateBeforeIcon
                        sx={{
                          fontSize: 20,
                          color: canNavLeft
                            ? "white"
                            : "rgba(255,255,255,0.4)",
                        }}
                      />
                    </IconButton>
                  )}

                  <Box
                    sx={{
                      position: "relative",
                      flex: 1,
                      borderRadius: 999,
                      padding: 0.25,
                      bgcolor: "rgba(0,0,0,0.7)",
                      border: "1px solid rgba(255,255,255,0.25)",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.8)",
                      overflow: "hidden",
                      display: "flex",
                    }}
                  >
                    {/* Sliding highlight bubble – per visible mode segment */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 3,
                        bottom: 3,
                        left: `${mobileHighlightLeft}%`,
                        width: `${mobileSegmentWidth}%`,
                        borderRadius: 999,
                        bgcolor: "rgba(255,255,255,0.16)",
                        boxShadow: "0 0 0 1px rgba(255,255,255,0.4)",
                        transition:
                          "left 0.22s ease-out, width 0.22s ease-out, background-color 0.22s ease-out",
                        pointerEvents: "none",
                      }}
                    />

                    {visibleModes.map((mode, visibleIdx) => {
                      const globalIndex = showModeNav
                        ? modeStartIndex + visibleIdx
                        : visibleIdx;
                      const isSelected = globalIndex === selectedModeIndex;
                      const isLast = visibleIdx === visibleModes.length - 1;
                      return (
                        <ButtonBase
                          key={mode.id}
                          onClick={() => onModeButtonClick(globalIndex)}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            px: 1.1,
                            py: 0.65,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.35,
                            borderRadius: 999,
                            transition: "all 0.18s ease-out",
                            position: "relative",
                            zIndex: 1,
                            borderRight: isLast
                              ? "none"
                              : "1px solid rgba(0,0,0,0.16)",
                            bgcolor: "transparent",
                            "&:hover": {
                              transform: "none",
                            },
                          }}
                        >
                          <Box
                            component="img"
                            src={mode.icon}
                            alt={mode.name}
                            sx={{
                              display: "block",
                              width: 24,
                              height: 24,
                              objectFit: "contain",
                              opacity: isSelected ? 1 : 0.8,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.65rem",
                              lineHeight: 1.2,
                              textAlign: "center",
                              opacity: isSelected ? 1 : 0.8,
                            }}
                          >
                            {mode.name}
                          </Typography>
                        </ButtonBase>
                      );
                    })}
                  </Box>

                  {/* Right nav (selects next mode) */}
                  {showModeNav && (
                    <IconButton
                      size="small"
                      onClick={() => handleModeNav("right")}
                      disabled={!canNavRight}
                      sx={{
                        bgcolor: "rgba(0,0,0,0.7)",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.95)" },
                        borderRadius: "999px",
                        p: 0.5,
                        visibility: showModeNav ? "visible" : "hidden",
                      }}
                    >
                      <NavigateNextIcon
                        sx={{
                          fontSize: 20,
                          color: canNavRight
                            ? "white"
                            : "rgba(255,255,255,0.4)",
                        }}
                      />
                    </IconButton>
                  )}
                </Box>
              ) : (
                // -------- DESKTOP VERSION ----------
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    mb: 2.5,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      display: "inline-flex",
                      borderRadius: 999,
                      padding: 0.25,
                      bgcolor: "rgba(0,0,0,0.7)",
                      border: "1px solid rgba(255,255,255,0.25)",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.8)",
                      overflow: "hidden",
                    }}
                  >
                    {/* Sliding highlight bubble – white-based */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 3,
                        bottom: 3,
                        left: `${segmentWidthDesktop * selectedModeIndex}%`,
                        width: `${segmentWidthDesktop}%`,
                        borderRadius: 999,
                        bgcolor: "rgba(255,255,255,0.16)",
                        boxShadow: "0 0 0 1px rgba(255,255,255,0.4)",
                        transition:
                          "left 0.22s ease-out, width 0.22s ease-out, background-color 0.22s ease-out",
                        pointerEvents: "none",
                      }}
                    />

                    {modes.map((mode, index) => {
                      const isSelected = index === selectedModeIndex;
                      const isLast = index === modes.length - 1;
                      return (
                        <ButtonBase
                          key={mode.id}
                          onClick={() => onModeButtonClick(index)}
                          sx={{
                            px: 1.3,
                            py: 0.75,
                            minWidth: 100,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.35,
                            borderRadius: 999,
                            transition: "all 0.18s ease-out",
                            position: "relative",
                            zIndex: 1,
                            borderRight: isLast
                              ? "none"
                              : "1px solid rgba(0,0,0,0.16)",
                            bgcolor: "transparent",
                            "&:hover": {
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          <Box
                            component="img"
                            src={mode.icon}
                            alt={mode.name}
                            sx={{
                              display: "block",
                              width: 30,
                              height: 30,
                              objectFit: "contain",
                              opacity: isSelected ? 1 : 0.8,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.7rem",
                              lineHeight: 1.2,
                              textAlign: "center",
                              opacity: isSelected ? 1 : 0.8,
                            }}
                          >
                            {mode.name}
                          </Typography>
                        </ButtonBase>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </>
          )}

          {/* Mode details – also centered */}
          {selectedMode && (
            <Box sx={{ maxWidth: 640, width: "100%" }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, fontSize: "0.85rem", mb: 0.25 }}
              >
                {selectedMode.name}
              </Typography>

              {selectedMode.realPedal && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    opacity: 0.8,
                    mb: 0.3,
                  }}
                >
                  <i>
                    Captured with its real-world {selectedMode.realPedal} counterpart.
                  </i>
                </Typography>
              )}

              {selectedMode.position && selectedMode.distance && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    opacity: 0.8,
                    mb: 1,
                  }}
                >
                  <i>
                    Settings: {selectedMode.position} · Extra:{" "}
                    {selectedMode.distance}
                  </i>
                </Typography>
              )}

              {/* Hidden audio elements */}
              {modes.map((mode, idx) => (
                <audio
                  key={mode.id || idx}
                  ref={getAudioRef(idx)}
                  preload="none"
                  src={mode.audio}
                  loop={isLooping}
                  playsInline
                  onLoadedMetadata={handleLoadedMeta}
                  style={{ display: "none" }}
                  aria-hidden
                />
              ))}

              {/* Transport */}
              <Stack spacing={1.5} sx={{ mt: 1, mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={togglePlay}
                    sx={{ textTransform: "none", fontSize: "0.75rem" }}
                  >
                    {isPlaying ? "Pause" : "Play"}
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={restartAll}
                    sx={{ textTransform: "none", fontSize: "0.75rem" }}
                  >
                    Restart
                  </Button>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ minWidth: 56, textAlign: "right", ml: "auto" }}
                  >
                    {fmt(time)} / {duration ? fmt(duration) : "--:--"}
                  </Typography>
                </Stack>

                {/* Seek bar */}
                <Box sx={{ px: 0.5 }}>
                  <input
                    type="range"
                    min={0}
                    max={duration || 0}
                    step={0.01}
                    value={isScrubbing ? undefined : time}
                    onChange={(e) => {
                      const t = parseFloat(e.target.value);
                      setIsScrubbing(true);
                      setTime(t); // UI only
                    }}
                    onMouseUp={(e) => {
                      const t = parseFloat(e.currentTarget.value);
                      setIsScrubbing(false);
                      commitSeek(t);
                    }}
                    onTouchEnd={(e) => {
                      const input = e.currentTarget;
                      const t = parseFloat(input.value);
                      setIsScrubbing(false);
                      commitSeek(t);
                    }}
                    onKeyUp={(e) => {
                      if (
                        ["ArrowLeft", "ArrowRight", "Home", "End"].includes(
                          e.key
                        )
                      ) {
                        const t = parseFloat(e.currentTarget.value);
                        setIsScrubbing(false);
                        commitSeek(t);
                      }
                    }}
                    style={{ width: "100%" }}
                    aria-label="Seek"
                  />
                </Box>

                {/* Loop */}
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    size="small"
                    variant={isLooping ? "contained" : "outlined"}
                    onClick={toggleLoop}
                    sx={{ textTransform: "none", fontSize: "0.75rem" }}
                  >
                    {isLooping ? "Loop: On" : "Loop: Off"}
                  </Button>
                </Stack>
              </Stack>

              {/* Descriptive text BELOW the player */}
              <Typography
                variant="body1"
                sx={{
                  opacity: 0.85,
                  color: theme.palette.text.secondary,
                  lineHeight: 1.5,
                  pb: 1,
                }}
              >
                {selectedMode.description}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2,
          py: 1,
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Button onClick={onClose} size="small" sx={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PedalDialog;
